import { Vue, Component, Prop, Ref, Watch } from 'vue-property-decorator'
import Highchart from '@/components/common/highchart/highchart.vue'
import { initialStatisticalAnalysisViewDetailChartState, StatisticalAnalysisViewDetailChartState } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart/statistical-analysis-view-detail-chart.model'
import StatisticalAnalysisViewDetailChartTypeCharts from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-type-charts/statistical-analysis-view-detail-chart-type-charts.vue'
import { Options } from 'highcharts'
import { StatisticalAnalysisViewDetailChartTypeChartsFactory } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-type-charts/statistical-analysis-view-detail-chart-type-charts-factory/statistical-analysis-view-detail-chart-type-charts-factory'
import { StatisticalAnalysisViewDetailChartTypeChartsTypes } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-type-charts/statistical-analysis-view-detail-chart-type-charts.model'
import StatisticalAnalysisViewDetailChartFilterAxis from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-filters-axis/statistical-analysis-view-detail-chart-filters-axis.vue'
import { StatisticalAnalysisDossiersChartResponsesTypes, StatisticalAnalysisDossiersExternalInfo, StatisticalAnalysisViewDetailParams } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { Nullable } from '@/shared/types/app.types'
import { StatisticalAnalysisViewDetailChartFilterAxisCombo, StatisticalAnalysisViewDetailChartFilterAxisXY } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-filters-axis/statistical-analysis-view-detail-chart-filters-axis.model'
import StatisticalAnalysisViewDetailChartFiltersExtended from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-filters-extended/statistical-analysis-view-detail-chart-filters-extended.vue'
import { StatisticalAnalysisViewDetailChartFilterByEnumAllowed, StatisticalAnalysisViewDetailChartFiltersExtendedFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-chart-filters-extended/statistical-analysis-view-detail-chart-filters-extended.model'
import { statisticalAnalysisHandlerFactory } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-handler.factory'
import StatisticalAnalysisSkeletonChart from '@/modules/statistical-analysis-module/components/statistical-analysis-skeletons/statistical-analysis-skeleton-chart/statistical-analysis-skeleton-chart.vue'

@Component({
    components: {
        Highchart,
        StatisticalAnalysisViewDetailChartTypeCharts,
        StatisticalAnalysisViewDetailChartFiltersExtended,
        StatisticalAnalysisViewDetailChartFilterAxis,
        StatisticalAnalysisSkeletonChart
    }
})
export default class StatisticalAnalysisViewDetailChart extends Vue {
    @Ref() readonly highCharts!: Highchart
    @Prop() readonly externalInfo!: StatisticalAnalysisDossiersExternalInfo
    @Prop() readonly viewDetailParams!: StatisticalAnalysisViewDetailParams
    @Prop(Object) readonly chartOptions!: Options
    @Prop({ default: true }) readonly displayXAxis!: boolean
    @Prop({ default: false }) readonly displayYAxis!: boolean
    @Watch('externalInfo', { deep: true })
    onTrackingSelectedChanged(value: StatisticalAnalysisDossiersExternalInfo) {
        if (value) {
            this._clearExtendedFilters()
            this.state.filters.externalFiltering.sidebarFilters = value
            this.instanceChart = statisticalAnalysisHandlerFactory.getChart(this.viewDetailParams.dossier, this.state.filters)!
            this.state.promiseChart = this._getChartData()
        }
    }
    state: StatisticalAnalysisViewDetailChartState
    instanceChart!: StatisticalAnalysisDossiersChartResponsesTypes

    constructor() {
        super()
        this.state = initialStatisticalAnalysisViewDetailChartState()
    }

    get axisChart(): StatisticalAnalysisViewDetailChartFilterAxisXY {
        return this.viewDetailParams?.chart.axis
    }

    get showExtendedFilters(): boolean {
        return this.viewDetailParams.chart.showExtendedFilters
    }

    created() {
        this.$eventHub.$on('vt-loading-view', this._emitEventVtGridLoading)
    }

    beforeDestroy() {
        this.$eventHub.$off('vt-loading-view', this._emitEventVtGridLoading)
    }

    mounted() {
        this.state = {
            ...this.state,
            filters: {
                ...this.state.filters,
                externalFiltering: {
                    ...this.state.filters.externalFiltering,
                    sidebarFilters: this.externalInfo
                },
                xAxis: {
                    groupBy1: this.viewDetailParams.chart.axis.xAxis.mainCombo?.selected?.id as string || null,
                    groupBy2: this.viewDetailParams.chart.axis.xAxis.associatedCombo?.selected?.id as string || null
                },
                yAxis: this.viewDetailParams.chart.axis.yAxis.mainCombo?.selected?.id as string || null
            }
        }

        this._reloadChart()
    }

    onUpdateTypeChart(newTypeChart: Options) {
        this.state.chartOptions = {
            ...newTypeChart,
            title: {
                text: this.state.filters.additionalFilterById?.id ? `${this.state.filters.additionalFilterBy?.label}: ${this.state.filters.additionalFilterById.label}` : ''
            }
        }
    }

    onSelectedTypeChart(newTypeChart: StatisticalAnalysisViewDetailChartTypeChartsTypes) {
        this.state.selectedTypeChart = newTypeChart
    }

    onChangeFilters(filters: StatisticalAnalysisViewDetailChartFiltersExtendedFilters): void {
        this.state = {
            ...this.state,
            filters: {
                ...this.state.filters,
                filterByIds: filters.mainFilter?.selected,
                additionalFilterBy: {
                    id: filters.filterBy?.selected?.id as StatisticalAnalysisViewDetailChartFilterByEnumAllowed,
                    label: filters.filterBy?.selected?.label!,
                    entityId: filters.filterBy?.selected?.entityId
                },
                additionalFilterById: {
                    id: filters.filterById?.selected?.id as number,
                    label: filters.filterById?.selected?.label!
                }
            }
        }
        this._reloadChart()
    }

    onChangeYAxisMainCombo(value: Nullable<StatisticalAnalysisViewDetailChartFilterAxisCombo>): void {
        this.state.filters.yAxis = value?.id
        this.state.promiseChart = this._getChartData()
    }

    async onChangeXAxisMainCombo(value: Nullable<StatisticalAnalysisViewDetailChartFilterAxisCombo>): Promise<void> {
        this.state.filters.xAxis.groupBy1 = value?.id
    }

    onChangeXAxisAssociatedCombo(value: Nullable<StatisticalAnalysisViewDetailChartFilterAxisCombo>): void {
        this.state.filters.xAxis.groupBy2 = value?.id
        this.state.promiseChart = this._getChartData()
    }

    private async _getChartData(): Promise<void> {
        const response = await this.$_toCallSecure<Options>(this.instanceChart.getChart())
        const chartConfiguration: Options = StatisticalAnalysisViewDetailChartTypeChartsFactory.getTypeChart(
            this.state.selectedTypeChart,
            response as Options
        )
        this.state = {
            ...this.state,
            chartOptions: {
                ...chartConfiguration,
                title: {
                    text: this.state.filters.additionalFilterById?.id ? `${this.state.filters.additionalFilterBy?.label}: ${this.state.filters.additionalFilterById.label}` : ''
                }
            }
        }
    }

    private _emitEventVtGridLoading(value: boolean): void {
        this.$eventHub.$emit('statistical-loading', value)
    }

    private _reloadChart(): void {
        this.instanceChart = statisticalAnalysisHandlerFactory.getChart(this.viewDetailParams.dossier, this.state.filters)!
        this.state.promiseChart = this._getChartData()
    }

    private _clearExtendedFilters(): void {
        const cleanedState = initialStatisticalAnalysisViewDetailChartState()
        this.state = {
            ...this.state,
            filters: {
                ...this.state.filters,
                filterByIds: cleanedState.filters.filterByIds,
                additionalFilterBy: cleanedState.filters.additionalFilterBy,
                additionalFilterById: cleanedState.filters.additionalFilterById
            }
        }
    }
}
